import { format } from 'date-fns';
import { de, enGB, ka, ko, pt, ru, uk, zhCN } from 'date-fns/locale';
import i18next from 'i18next';
import Cookies from 'universal-cookie';
import { RpcError } from '@protobuf-ts/runtime-rpc';
import { Account_Info, Account_Profile_Ava, Matrix_Position, TreeAccount } from '../generated/mlmbox/types/account';
import i18n from '../i18n';
import { setAvatars } from '../store/avatars/avatars.slice';
import { store } from '../store/store';
import { transport } from './constants';
import { AccountClient } from '../generated/mlmbox/client/account/account.client';
import { Relationship, Relationship_Kind } from '../generated/mlmbox/types/relationship';

declare global {
    interface Window {
        Telegram: any;
    }
}

export function formatNumberString(input: string): string {
    const number = parseFloat(input);

    if (isNaN(number)) {
        throw new Error('Input must be a valid number string');
    }

    if (number >= 1_000_000_000) {
        return `${(number / 1_000_000_000).toFixed(1)}B`; // B - миллиард
    } else if (number >= 1_000_000) {
        return `${(number / 1_000_000).toFixed(1)}M`; // M - миллион
    } else if (number >= 1_000) {
        return `${(number / 1_000).toFixed(1)}K`; // K - тысяча
    } else {
        return number.toFixed(0); // Убираем лишние нули
    }
}

export enum haptic {
    light = 'light',
    medium = 'medium',
}

export function Haptick(stage: haptic) {
    window?.Telegram?.WebApp?.HapticFeedback?.impactOccurred(stage);
    return stage;
}

export enum type {
    success = 'success',
    error = 'error',
    warning = 'warning',
}

export function Notification(type: type) {
    window?.Telegram?.WebApp?.HapticFeedback?.notificationOccurred(type);
    return type;
}

export async function copyToClipboard(text: string) {
    try {
        await navigator.clipboard.writeText(text);
    } catch (err) {
        console.error('Не удалось скопировать: ', err);
    }
}

export class TreeNode {
    id: number | null;
    children: TreeNode[];
    constructor(id: number | null) {
        this.id = id;
        this.children = [];
    }
}

export function onImagesLoad(imageSources: string[], callback: Function) {
    const sources = Array.isArray(imageSources) ? imageSources : [imageSources];

    const imagePromises = sources.map((src) => {
        return new Promise((resolve) => {
            const image = new Image();

            image.src = src;
            image.onload = () => resolve('');
            image.onerror = () => resolve(''); // Resolve even if image fails to load
        });
    });

    Promise.all(imagePromises).then(() => {
        callback();
    });
}

export function fillTree(arr: Matrix_Position[], position: number) {
    if (arr.length === 0) return new TreeNode(position);

    const root = new TreeNode(position);

    function addChildren(node: TreeNode, arr: Matrix_Position[], width: number) {
        let count = 0;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i].positionRefId === node.id && count < width) {
                const childNode = new TreeNode(arr[i].positionId);

                node.children.push(childNode);
                count++;
                addChildren(childNode, arr, arr[i].positionWidth);
            }
        }
    }

    addChildren(root, arr, arr[0].positionWidth);
    return root;
}

export function ensureMinimumChildren(node: TreeNode) {
    let count = 0;
    if (count > 2) return;
    console.log('call');
    if (node.children.length < 2) {
        while (node.children.length < 2) {
            node.children.push(new TreeNode(null));
        }
    }

    for (let child of node.children) {
        if (child !== null) {
            ensureMinimumChildren(child);
        }
        count++;
    }
    return count;
}

export function checkUserLoggedIn() {
    const cookies = new Cookies();
    const jwt_token = cookies.get('_tv1');

    if (jwt_token) {
        return true;
    }

    return false;
}

type Group = {
    id: number;
    title: string;
};

type Tree = {
    id: number;
    title: string;
    groupId: number;
    defaultPositionWidth: number;
    amountAssetId: number;
    amountValue: string;
};

type Position = {
    positionId: number;
    positionRefId: number;
    positionRefLine: number;
    positionWidth: number;
    accountId: number;
    treeAccountId: number;
    reinvestEnabled: boolean;
    relationship?: {
        kind: number;
        level: number;
        branch: number;
    };
    ava?: {
        collectionId: number;
        entityId: number;
    } | null;
};

export type PositionNode = {
    position: Position;
    children: PositionNode[];
};

export class ViewState {
    private groups: Map<number, Group> = new Map();
    private trees: Map<number, Tree> = new Map();
    private positions: Map<number, Position> = new Map();
    private indexPositionRef: Map<string, number> = new Map();

    constructor() {}

    addGroup(group: Group): void {
        this.groups.set(group.id, group);
    }

    addTree(tree: Tree): void {
        this.trees.set(tree.id, tree);
    }

    addPosition(position: Position): void {
        this.indexPositionRef.set(`${position.positionRefId},${position.positionRefLine}`, position.positionId);
        this.positions.set(position.positionId, position);
    }

    getGroups(): Group[] {
        return Array.from(this.groups.values()).sort((a, b) => a.id - b.id);
    }

    getTrees(): Tree[] {
        return Array.from(this.trees.values()).sort((a, b) => a.id - b.id);
    }

    getTreesByGroupId(groupId: number): Tree[] {
        if (groupId > 0) {
            return Array.from(this.trees.values())
                .filter((tree) => tree.groupId === groupId)
                .sort((a, b) => a.id - b.id);
        }
        return [];
    }

    private defaultPosition(positionRefId: number, positionRefLine: number, positionWidth: number): Position {
        return {
            positionId: 0,
            positionRefId,
            positionRefLine,
            positionWidth,
            accountId: 0,
            treeAccountId: 0,
            reinvestEnabled: false,
            ava: {
                collectionId: 1,
                entityId: 1,
            },
            relationship: {
                kind: 0,
                level: 0,
                branch: 0,
            },
        };
    }

    private cacheViewChildren(positionRef: Position, defaultPositionWidth: number): Position[] {
        if (positionRef.positionWidth > 0) {
            const children: Position[] = [];
            const positionRefId = positionRef.positionId;
            if (positionRefId > 0) {
                for (let positionRefLine = 1; positionRefLine <= positionRef.positionWidth; positionRefLine++) {
                    const key = `${positionRefId},${positionRefLine}`;
                    const nextPositionId = this.indexPositionRef.get(key);
                    children.push(
                        nextPositionId !== undefined && this.positions.has(nextPositionId)
                            ? this.positions.get(nextPositionId)!
                            : this.defaultPosition(positionRefId, positionRefLine, defaultPositionWidth)
                    );
                }
            } else {
                for (let positionRefLine = 1; positionRefLine <= positionRef.positionWidth; positionRefLine++) {
                    children.push(this.defaultPosition(0, positionRefLine, defaultPositionWidth));
                }
            }
            return children;
        }
        return [];
    }

    private cacheViewTeam(positionRef: Position, defaultPositionWidth: number, level: number): PositionNode {
        const result: PositionNode = {
            position: positionRef,
            children: [],
        };
        if (level > 0) {
            this.cacheViewChildren(result.position, defaultPositionWidth).forEach((position) => {
                result.children.push(this.cacheViewTeam(position, defaultPositionWidth, level - 1));
            });
        }
        return result;
    }

    cacheView(treeId: number, positionId: number, level: number): PositionNode | undefined {
        const tree = this.trees.get(treeId);
        const position = this.positions.get(positionId);

        if (tree && position) {
            return this.cacheViewTeam(position, tree.defaultPositionWidth, level);
        }
        return undefined;
    }
}

export function welcomeTtm() {
    return console.log(
        '%cTTM%cPartners',
        `
                height: 50px;
                display: flex;
                align-items: center;
                border-radius: 37.37px;
                border: 0.84px solid #0462f9;
                font-size: 30px;
                padding: 10px 20px;
                font-family: monospace;
                font-weight: 500;
                border-radius: 37.37px;
                background: linear-gradient(0deg, rgba(72, 160, 247, 0.7) 0%, rgba(72, 160, 247, 0.7) 100%), linear-gradient(93deg, #0462f9 32.99%, #101598 100%);
          `,
        `
                background-color: transparent;
                height: 40px;
                display: flex;
                align-items: center;
                gap: 5px;
                border-radius: 0 37.37px 37.37px 0;
                border: 0.84px solid #0462f9;
                font-size: 30px;
                padding: 10px 20px 10px 40px;
                border-left: none;
                transform: translateX(-100px);
                margin-left: -25px;
          `
    );
}

export function getColor(relationship?: Relationship): string | undefined {
    if (!relationship?.kind) return;

    switch (relationship.kind as Relationship_Kind) {
        case Relationship_Kind.OUTSIDER:
            return '#BABABA';
        case Relationship_Kind.PERSONAL:
            return '#FF7A00';
        case Relationship_Kind.REFERRAL:
            return '#E53A3A';
        case Relationship_Kind.TEAM:
            return relationship.level === 1 ? '#79CB5C' : '#0075FF';
        case Relationship_Kind.UNKNOWN:
            return '#BABABA';
        default:
            return '#000';
    }
}

const locales = {
    ru: ru,
    en: enGB,
    uk: uk,
    zh: zhCN,
    pt: pt,
    ko: ko,
    ka: ka,
    de: de,
};

export function getDate(date: number): string {
    const regDate = new Date(date * 1000);
    const lng = i18n.resolvedLanguage === 'en' ? 'en' : 'ru';
    const result = format(regDate, 'd MMMM yyyy', { locale: locales[lng] });
    return result;
}

export function isRpcError(error: unknown): error is RpcError {
    return typeof error === 'object' && error !== null && 'code' in error && 'message' in error;
}

// export async function setAvas(itemsList: Account.Info.AsObject[]) {
//     if (itemsList.length === 0) return;
//     if (!localStorage.getItem('ava_1')) localStorage.setItem('ava_1', JSON.stringify({}));

//     const storage = localStorage.getItem('ava_1');
//     if (!storage) return undefined;

//     const imageCache = JSON.parse(storage);

//     const accountCLient = new AccountClient(API_URL, null, null);
//     const req = new Account.Profile.Ava.List();
//     const avas: Account.Profile.Ava[] = [];
//     for (const el of itemsList) {
//         const cacheKey = `${el.profile?.ava?.collectionId}-${el.profile?.ava?.entityId}`;
//         if (imageCache[cacheKey]) {
//             continue;
//         }
//         const ava = new Account.Profile.Ava();
//         if (el.profile?.ava?.collectionId && el.profile?.ava?.entityId) {
//             ava.setEntityId(el.profile?.ava?.entityId);
//             ava.setCollectionId(el.profile?.ava?.collectionId);
//             avas.push(ava);
//         }
//     }

//     if (avas.length > 0) {
//         req.setItemsList(avas);

//         const response = await accountCLient.getAvaFiles(req, { authorization: getToken() });

//         for (const el of response.toObject().itemsList) {
//             const cacheKey = `${el.collectionId}-${el.entityId}`;
//             if (imageCache[cacheKey]) {
//                 continue;
//             }
//             imageCache[cacheKey] = el.value;
//         }

//         localStorage.setItem('ava_1', JSON.stringify(imageCache));
//     }
// }

// export async function getAva(collectionId: number, entityId: number): Promise<string | undefined> {
//     // if (!localStorage.getItem('ava_1')) localStorage.setItem('ava_1', JSON.stringify({}));
//     const cacheKey = `${collectionId}-${entityId}`;
//     const storage = localStorage.getItem('ava_1');
//     if (!storage) return undefined;

//     const imageCache = JSON.parse(storage);
//     if (imageCache[cacheKey]) {
//         return imageCache[cacheKey];
//     }
// }

export async function setAvas(itemsList: Account_Info[]) {
    if (itemsList.length === 0) return;
    const avatars = store.getState().avatars.value;
    if (!avatars) return undefined;

    const imageCache = JSON.parse(JSON.stringify(avatars));

    const accountCLient = new AccountClient(transport);
    const avatarsList: Account_Profile_Ava[] = [];

    for (const el of itemsList) {
        const cacheKey = `${el.profile?.ava?.collectionId}-${el.profile?.ava?.entityId}`;
        if (imageCache[cacheKey]) {
            continue;
        }
        if (el.profile?.ava?.collectionId && el.profile?.ava?.entityId) {
            avatarsList.push({ collectionId: el.profile?.ava?.collectionId, entityId: el.profile?.ava?.entityId });
        }
    }

    if (avatarsList.length > 0) {
        const data = await accountCLient.getAvaFiles({ items: avatarsList }, { meta: { authorization: getToken() } });

        if (data.status.code === 'OK') {
            for (const el of data.response.items) {
                const cacheKey = `${el.collectionId}-${el.entityId}`;
                if (imageCache[cacheKey]) {
                    continue;
                }
                imageCache[cacheKey] = el.value;
            }

            await store.dispatch(setAvatars(imageCache));
        }
    }
}

export async function getAva(collectionId: number, entityId: number): Promise<string | undefined> {
    const cacheKey = `${collectionId}-${entityId}`;
    const avatars = store.getState().avatars.value;
    if (!avatars) return undefined;

    if (avatars[cacheKey]) {
        return avatars[cacheKey];
    }
}

export function filterNonEmptyMatrixTree(matrixTreeAccounts: TreeAccount[]): TreeAccount[] {
    return matrixTreeAccounts.filter((account) => !(account.bookingQuantity === 0 && account.entities.length === 0));
}

export function getName(account: Account_Info): string {
    let result = '';

    if (account.channels.includes(1)) {
        if (account.profile?.firstName?.length === 0 && account.profile?.lastName?.length === 0)
            return (result = i18next.t('unknown_duck'));

        return (result = `${account.profile?.firstName} ${' '} ${account.profile?.lastName}`);
    }

    if (account.profile?.telegram?.username?.length) return (result = `${account.profile?.telegram?.username}`);

    if (account?.channels.includes(2) || account?.channels.includes(3)) return (result = i18next.t('unknown_duck'));

    return result;
}

export function getLoginOrId(user: Account_Info): string {
    if (user.account?.login) return `@${user.account.login}`;

    if (user.account?.id) return `id${user.account?.id}`;

    return '';
}

export function getNameOrId(user: Account_Info): string {
    if (user.profile?.firstName) return `${user.profile?.firstName}  ${' '}  ${user.profile.lastName}`;

    if (user.account?.login) return `@${user.account.login}`;

    if (user.account?.id) return `id${user.account?.id}`;

    return '';
}

export function getToken() {
    return store.getState().auth.value.isAuth;
    // return 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJwaWQiOiJUVE1fSE9MRElORyIsImlzcyI6Miwic3ViIjoxLCJ0Z2lkIjo4MTQyNzI0NDksImV4cCI6MTcyOTA4NTA3MiwiaWF0IjoxNzI4NDgwMjcyfQ.keIP5hzZX-6bBtYJ4_A_hZms7ayB55t5hByOB9f6IIE';
    // return 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJwaWQiOiJUVE1fSE9MRElORyIsImlzcyI6MSwic3ViIjoyLCJ0Z2lkIjo4MTQyNzI0NDksImV4cCI6MTcyODM0MTA4NSwiaWF0IjoxNzI3NjQ5ODg1fQ.7fbrJd6mrwwOI__aC9YtkwXpB0GWqM0TVS_tZfMSDxw';
}

export function isAdmin(id: number) {
    const admins = [1, 2, 85475, 22911, 84245, 86047];
    if (admins.includes(id)) {
        return true;
    }

    return false;
}
